import { z } from 'zod';
import { HexColor } from './colors';
import { valueIssue } from './values';
import { WorkspaceModule } from './workspace-modules';

export const MemberStatus = z.enum(['active', 'invited', 'deactivated']);
export type MemberStatus = z.infer<typeof MemberStatus>;

export const MemberDTO = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  initials: z.string(),
  avatarColor: HexColor,
  title: z.string().nullable(),
  roleKey: z.string(),
  roleLabel: z.string(),
  status: MemberStatus,
});
export type MemberDTO = z.infer<typeof MemberDTO>;

export const WorkspaceDTO = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  environment: z.string(),
  locale: z.string(),
  timezone: z.string(),
  currency: z.string(),
});
export type WorkspaceDTO = z.infer<typeof WorkspaceDTO>;

export const UiLocale = z.enum(['en', 'de']);
export type UiLocale = z.infer<typeof UiLocale>;
export const NumberFormat = z.enum(['de', 'en', 'ch', 'fr']);
export type NumberFormat = z.infer<typeof NumberFormat>;
export const DateFormat = z.enum(['dmy_dot', 'ymd_dash', 'mdy_slash', 'dmy_slash']);
export type DateFormat = z.infer<typeof DateFormat>;

/** An IANA time zone the runtime knows ("Europe/Berlin"). */
export const TimeZone = z
  .string()
  .min(1)
  .max(64)
  .refine((tz) => isValidTimeZone(tz), valueIssue('invalid_time_zone'));

/** Colour scheme of the app: `system` follows the operating system (account-settings). */
export const ThemePreference = z.enum(['system', 'light', 'dark']);
export type ThemePreference = z.infer<typeof ThemePreference>;

/**
 * The page the app opens on (a fresh tab or after signing in): Home, the Inbox, the Library, or the
 * page the member was on last in this browser.
 */
export const StartPage = z.enum(['home', 'inbox', 'library', 'last']);
export type StartPage = z.infer<typeof StartPage>;

/** The member's own choices; null follows the workspace default. */
export const MemberPreferencesDTO = z.object({
  locale: UiLocale.nullable(),
  timezone: z.string().nullable(),
  /** Own number format; null follows the language, then Settings › General (account-settings). */
  numberFormat: NumberFormat.nullable().default(null),
  /** Own date format; null follows Settings › General, then the language. */
  dateFormat: DateFormat.nullable().default(null),
  theme: ThemePreference.default('system'),
  startPage: StartPage.default('home'),
});
export type MemberPreferencesDTO = z.infer<typeof MemberPreferencesDTO>;

export const UpdateMemberPreferencesInput = z
  .object({
    locale: UiLocale.nullable(),
    timezone: TimeZone.nullable(),
    numberFormat: NumberFormat.nullable(),
    dateFormat: DateFormat.nullable(),
    theme: ThemePreference,
    startPage: StartPage,
  })
  .partial()
  .strict();
export type UpdateMemberPreferencesInput = z.infer<typeof UpdateMemberPreferencesInput>;

/**
 * The shape of `PATCH /me/preferences` checked before authentication context exists; the service
 * validates the values (`UpdateMemberPreferencesInput`) in the member's language.
 */
export const UpdateMemberPreferencesBody = z
  .object({
    locale: UiLocale.nullable(),
    timezone: z.string().min(1).max(64).nullable(),
    numberFormat: NumberFormat.nullable(),
    dateFormat: DateFormat.nullable(),
    theme: ThemePreference,
    startPage: StartPage,
  })
  .partial()
  .strict();

/**
 * Avatar colours a member can choose (Account › Profile). White initials stay readable on each
 * (`avatarBackground` of the UI kit darkens lighter hues further when needed).
 */
export const AVATAR_COLORS = [
  '#444CE7',
  '#1570EF',
  '#0086C9',
  '#0E9384',
  '#099250',
  '#4CA30D',
  '#CA8504',
  '#E04F16',
  '#E31B54',
  '#DD2590',
  '#7A5AF8',
  '#475467',
] as const;

const CurrencyCode = z.string().regex(/^[A-Z]{3}$/);
const RegionCode = z.string().regex(/^[A-Z]{2}$/);

/** What the web app applies: the member's preference, else the workspace default. */
export const EffectiveFormatsDTO = z.object({
  locale: UiLocale,
  timezone: z.string(),
  numberFormat: NumberFormat,
  dateFormat: DateFormat,
  fiscalYearStart: z.number().int().min(1).max(12),
  /** Workspace default currency (typed-values; absent in responses of older endpoints). */
  currency: CurrencyCode.optional(),
  /** Region for phone numbers and postal addresses, derived from the workspace time zone. */
  region: RegionCode.optional(),
  /** 1 = Monday, 0 = Sunday. */
  weekStartsOn: z.union([z.literal(0), z.literal(1)]).optional(),
});
export type EffectiveFormatsDTO = z.infer<typeof EffectiveFormatsDTO>;

export const MeDTO = z.object({
  member: MemberDTO,
  workspace: WorkspaceDTO,
  organization: z.object({ id: z.string(), name: z.string() }),
  permissions: z.array(z.string()),
  /** The optional modules turned on in the workspace (Settings › General › Modules). */
  modules: z.array(WorkspaceModule),
  /**
   * An instance operator: an administrator of the installation's operator workspace, who runs the
   * whole installation (job processing, storage, license and updates); other admins run their workspace.
   */
  operator: z.boolean().default(false),
  preferences: MemberPreferencesDTO,
  formats: EffectiveFormatsDTO,
  /**
   * The number and date format that apply while the member has none of their own (their language, then
   * Settings › General): Account › Preferences names them as the default.
   */
  formatDefaults: z.object({ numberFormat: NumberFormat, dateFormat: DateFormat }).optional(),
  /** The server's business clock (`clock.now()`) when the response was built. */
  serverNow: z.iso.datetime().optional(),
  /** True when the server clock is frozen (DR_NOW_FROZEN=1, development and e2e only). */
  clockFrozen: z.boolean().optional(),
});
export type MeDTO = z.infer<typeof MeDTO>;

// -- locale formats (typed-values) ----------------------------------------------------------

/**
 * Everything value formatting and parsing needs about the person it is for: the language of
 * words (month names, messages), the number and date conventions, the time zone, the default
 * currency and the region (phone numbers, postal addresses). Carried by `ExecContext.formats` on
 * the server and by `lib/locale.ts` in the web app; resolved by `resolveLocaleFormats`.
 */
export const LocaleFormats = z.object({
  language: UiLocale,
  numberFormat: NumberFormat,
  dateFormat: DateFormat,
  timeZone: z.string().min(1).max(64),
  currency: CurrencyCode,
  region: RegionCode,
  weekStartsOn: z.union([z.literal(0), z.literal(1)]),
  fiscalYearStart: z.number().int().min(1).max(12),
});
export type LocaleFormats = z.infer<typeof LocaleFormats>;

/** BCP 47 tag of each number format, for `Intl.NumberFormat`. */
export const NUMBER_FORMAT_LOCALE: Record<NumberFormat, string> = {
  de: 'de-DE',
  en: 'en-US',
  ch: 'de-CH',
  fr: 'fr-FR',
};

/** The number and date conventions a language implies when nothing else was chosen. */
export const LANGUAGE_FORMATS: Record<UiLocale, { numberFormat: NumberFormat; dateFormat: DateFormat }> = {
  en: { numberFormat: 'en', dateFormat: 'mdy_slash' },
  de: { numberFormat: 'de', dateFormat: 'dmy_dot' },
};

/** Region of the common IANA time zones (phone numbers, postal formats, week start). */
const ZONE_REGION: Record<string, string> = {
  'Europe/Berlin': 'DE',
  'Europe/Busingen': 'DE',
  'Europe/Vienna': 'AT',
  'Europe/Zurich': 'CH',
  'Europe/Vaduz': 'LI',
  'Europe/Luxembourg': 'LU',
  'Europe/Brussels': 'BE',
  'Europe/Amsterdam': 'NL',
  'Europe/Paris': 'FR',
  'Europe/Monaco': 'MC',
  'Europe/Madrid': 'ES',
  'Europe/Lisbon': 'PT',
  'Europe/Rome': 'IT',
  'Europe/London': 'GB',
  'Europe/Dublin': 'IE',
  'Europe/Copenhagen': 'DK',
  'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO',
  'Europe/Helsinki': 'FI',
  'Europe/Warsaw': 'PL',
  'Europe/Prague': 'CZ',
  'Europe/Budapest': 'HU',
  'Europe/Istanbul': 'TR',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Phoenix': 'US',
  'America/Los_Angeles': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Mexico_City': 'MX',
  'America/Sao_Paulo': 'BR',
  'Asia/Tokyo': 'JP',
  'Asia/Shanghai': 'CN',
  'Asia/Singapore': 'SG',
  'Asia/Kolkata': 'IN',
  'Asia/Dubai': 'AE',
  'Australia/Sydney': 'AU',
};

/** Region code for a time zone (falls back to the number format's country, then DE). */
export function regionForTimeZone(timeZone: string, numberFormat?: NumberFormat): string {
  const region = ZONE_REGION[timeZone];
  if (region) return region;
  if (numberFormat === 'ch') return 'CH';
  if (numberFormat === 'fr') return 'FR';
  if (numberFormat === 'en') return timeZone.startsWith('America/') ? 'US' : 'GB';
  return 'DE';
}

const SUNDAY_REGIONS = new Set(['US', 'CA', 'MX', 'BR', 'JP', 'IL', 'IN', 'AU']);

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * The effective formats of a member (or of the workspace, for API keys and system work).
 *
 * - Language and time zone: the member's own choice, else the workspace default.
 * - Number format: the member's own choice; else, when the member chose a language of their own,
 *   that language's grouping (an English UI shows `€310,000.00`, never a misread `€310.000,00`);
 *   else the workspace's number format from Settings › General.
 * - Date format: the member's own choice, else the workspace's (an organisation convention admins
 *   set with a preview; `29.09.2026` for everyone in a German workspace), else the language's.
 * - Currency: the workspace currency. Region and week start: derived from the workspace zone.
 */
export function resolveLocaleFormats(input: {
  workspace: { locale: string; timezone: string; currency: string };
  general?: { numberFormat?: unknown; dateFormat?: unknown; fiscalYearStart?: unknown } | null;
  member?: {
    locale?: string | null;
    timezone?: string | null;
    numberFormat?: string | null;
    dateFormat?: string | null;
  } | null;
}): LocaleFormats {
  const { workspace, general, member } = input;
  const memberLanguage = UiLocale.safeParse(member?.locale).data ?? null;
  const language = memberLanguage ?? UiLocale.safeParse(workspace.locale).data ?? 'en';
  const fallback = memberLanguage ? LANGUAGE_FORMATS[memberLanguage] : null;
  const numberFormat =
    NumberFormat.safeParse(member?.numberFormat).data ??
    fallback?.numberFormat ??
    NumberFormat.safeParse(general?.numberFormat).data ??
    LANGUAGE_FORMATS[language].numberFormat;
  const dateFormat =
    DateFormat.safeParse(member?.dateFormat).data ??
    DateFormat.safeParse(general?.dateFormat).data ??
    LANGUAGE_FORMATS[language].dateFormat;
  const zoneChoices = [member?.timezone, workspace.timezone, 'UTC'];
  const timeZone = zoneChoices.find((z): z is string => !!z && isValidTimeZone(z)) ?? 'UTC';
  const workspaceZone = isValidTimeZone(workspace.timezone) ? workspace.timezone : timeZone;
  const region = regionForTimeZone(workspaceZone, numberFormat);
  const fiscal = Number(general?.fiscalYearStart);
  return {
    language,
    numberFormat,
    dateFormat,
    timeZone,
    currency: /^[A-Z]{3}$/.test(workspace.currency) ? workspace.currency : 'EUR',
    region,
    weekStartsOn: SUNDAY_REGIONS.has(region) ? 0 : 1,
    fiscalYearStart: Number.isInteger(fiscal) && fiscal >= 1 && fiscal <= 12 ? fiscal : 1,
  };
}

/** Formats for a language alone (before sign-in, system work without a workspace). */
export function defaultLocaleFormats(language: UiLocale = 'en', timeZone = 'UTC'): LocaleFormats {
  return resolveLocaleFormats({
    workspace: { locale: language, timezone: timeZone, currency: 'EUR' },
    member: { locale: language },
  });
}

/** The `/me` formats block of resolved locale formats. */
export function effectiveFormatsOf(f: LocaleFormats): EffectiveFormatsDTO {
  return {
    locale: f.language,
    timezone: f.timeZone,
    numberFormat: f.numberFormat,
    dateFormat: f.dateFormat,
    fiscalYearStart: f.fiscalYearStart,
    currency: f.currency,
    region: f.region,
    weekStartsOn: f.weekStartsOn,
  };
}

export const DevLoginInput = z.object({ email: z.email() });
export type DevLoginInput = z.infer<typeof DevLoginInput>;

export const DevUserDTO = MemberDTO.pick({
  id: true,
  name: true,
  email: true,
  initials: true,
  avatarColor: true,
  roleLabel: true,
}).extend({
  /** The workspace the dev login signs into (the person's oldest active membership). */
  workspaceName: z.string(),
  /** Further workspaces the same person (email) belongs to; switchable after signing in. */
  otherWorkspaces: z.number().int().min(0),
});
export type DevUserDTO = z.infer<typeof DevUserDTO>;

/** Well-known permissions of the foundation. S10 extends the model (object/field/record policies). */
export const PERMISSIONS = {
  admin: 'workspace.admin',
  metadataWrite: 'metadata.write',
  dashboardsCreate: 'dashboards.create',
  recordsRead: 'records.read',
  recordsWrite: 'records.write',
  recordsDelete: 'records.delete',
  recordsExport: 'records.export',
} as const;
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
